import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import AddShipmentModalPropTypes from "./AddShipmentModalPropTypes";
import { flagsAndExperimentNames, PaymentMethods, APPLE_PAY_STRING } from "rtr-constants";
import { withFlagsAndExperiments } from "components/source/hoc/with-flags-and-experiments";

import Modal from "components/design-system/modal";
import RTRButton from "components/design-system/rtr-button";
import SummaryItem from "components/source/checkout/summary/summary-item";

import AtomCreditCardIcon from "components/source/atoms/atom-credit-card-icon";
import ExpressBagPromoCode from "components/source/checkout/bag/membership/express-bag-promo-code";
import ApplePaySectionMembership from "components/source/membership/apple-pay-section-membership";

import MembershipHelpers from "helpers/membership-helpers";
import { paymentProfileDisplayString } from "helpers/payment-method-helpers";
import { formattedPrice, priceStringIntoFloat, isZeroAmount } from "helpers/invoice-helper";
import { retailPriceFormatted } from "helpers/PricingHelper";
import { evaluateApplePayOptions, PAYMENT_OPTION_RULE } from "helpers/payment-options-helper";
import { User } from "routes";

import { fetchBag } from "actions/membership-bag-actions";
import FlagsAndExperimentsActions from "actions/flags-and-experiments-actions";
import ExclamationIcon from "images/sprites/reb/svg/exclamation_icon.svg";
import QuestionMarkIcon from "images/sprites/reb/svg/question-mark.svg";

const sectionContainerBaseClass = "flex flex-col w-full p-5 gap-2";
const sectionHeaderBaseClass = "text-lg font-normal";

export class AddShipmentModal extends React.Component {
  static propTypes = {
    ...AddShipmentModalPropTypes,
  };

  state = {
    planInfoSectionIsExpanded: false,
    upgradeTier: null,
    isCtaEnabled: true,
    isCTALoading: false,
    isApplePayEnabled: false,
    evaluationComplete: false,
    ctaText: "Update Subscription",
    disableCtaBtn: false,
    displayCtaInternal: false,
  };

  componentDidMount() {
    const {
      getMembershipTiers,
      membershipBag,
      membershipTiers = [],
      membershipState,
      oopsModal,
      fetchLegalText,
      fetchMembershipBag,
      fetchFlags,
    } = this.props;

    // Don't make any calls if ineligible
    if (!MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
      return;
    } else {
      fetchLegalText();
    }

    if (!membershipBag?.paymentProfiles) {
      fetchMembershipBag();
    }

    if (membershipTiers.length > 0) {
      this.fetchNewPlan();
    } else {
      // Fetch membershipTiers if they have not yet been populated
      getMembershipTiers().then(
        () => {
          this.fetchNewPlan();
        },
        () => {
          oopsModal();
        }
      );
    }
    fetchFlags();
  }

  componentDidUpdate(prevProps) {
    const {
      immediateUpgradePlanPreviewLoading,
      membershipState,
      membershipState: { monthlyShipmentLimit } = {},
    } = this.props;

    if (prevProps.immediateUpgradePlanPreviewLoading && !immediateUpgradePlanPreviewLoading) {
      this.processPrices();
    }

    if (
      prevProps.membershipState.monthlyShipmentLimit !== monthlyShipmentLimit &&
      MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)
    ) {
      this.fetchNewPlan();
    }
  }

  // data fetchers/processors
  getUpgradeTier = () => {
    const { membershipState: { monthlyShipmentLimit } = {}, membershipTiers = [] } = this.props;
    const newTier = membershipTiers.filter(tier => tier.monthlyShipmentLimit === monthlyShipmentLimit + 1);

    if (newTier.length === 1 && newTier[0].id) {
      return newTier[0];
    }

    return null;
  };

  fetchNewPlan() {
    const { getChangePlanPreview } = this.props;

    const upgradeTier = this.getUpgradeTier();
    this.setState({ upgradeTier: upgradeTier });
    if (upgradeTier?.id) {
      getChangePlanPreview(upgradeTier.id);
    }
  }

  getApplePayLineItems = () => {
    if (!this.state?.total) return null;

    return [
      {
        label: "Subtotal",
        amount: priceStringIntoFloat(this.state.promo) < 0 ? this.state.prePromo : this.state?.preTax,
      },
      { label: "Promo", amount: priceStringIntoFloat(this.state.promo) < 0 ? this.state.promo : "USD 0.00" },
      { label: "Tax", amount: this.state.tax },
      { label: "Total", amount: this.state.total, type: "total" },
    ];
  };

  processPrices = () => {
    const { immediateUpgradePlanPreview } = this.props;
    const invoice = immediateUpgradePlanPreview?.membershipTierChangeCharges?.[0]?.totals;
    if (invoice) {
      const pricing = {};
      invoice
        .filter(
          total =>
            total.type === "sub" ||
            total.type === "preTax" ||
            total.type === "tax" ||
            total.type === "total" ||
            total.type === "promo"
        )
        .forEach(total => (pricing[total.type] = total.amount));

      pricing.promoTermCount = immediateUpgradePlanPreview?.promoTermCount;
      pricing.prePromo = pricing.promo
        ? `USD ${priceStringIntoFloat(pricing.preTax) - priceStringIntoFloat(pricing.promo)}.00`
        : null;
      this.setState({ ...pricing, invoiceIncludeFailed: false });
    } else {
      this.setState({ invoiceIncludeFailed: true });
    }
  };

  evaluatePaymentOptions() {
    const evaluation = evaluateApplePayOptions(this.props.membershipBag?.paymentProfiles, !!window?.ApplePaySession);
    if (evaluation === PAYMENT_OPTION_RULE.DISPLAY_APPLE_PAY) {
      this.setState({
        isCtaEnabled: false,
        isApplePayEnabled: true,
        evaluationComplete: true,
      });
      return;
    }

    if (evaluation === PAYMENT_OPTION_RULE.UPDATE_BILLING) {
      this.setState({
        isCtaEnabled: true,
        isApplePayEnabled: false,
        ctaText: "UPDATE BILLING METHOD",
        disableCtaBtn: true,
        evaluationComplete: true,
      });
      return;
    }

    if (evaluation === PAYMENT_OPTION_RULE.DISPLAY_BOTH) {
      this.setState({
        isCtaEnabled: false,
        isApplePayEnabled: true,
        evaluationComplete: true,
        ctaText: "Update Subscription With Card on File",
        displayCtaInternal: true,
      });
      return;
    }

    if (evaluation === PAYMENT_OPTION_RULE.DISPLAY_COF) {
      this.setState({
        isCtaEnabled: true,
        isApplePayEnabled: false,
        evaluationComplete: true,
      });
    }
  }

  // event handlers

  togglePlanInfoExpandedState = () => {
    const planIsExpanding = !this.state.planInfoSectionIsExpanded;

    this.setState({
      planInfoSectionIsExpanded: planIsExpanding,
    });
  };

  onDismissModal = () => {
    const { dismissModal } = this.props;
    this.setState({ isCTALoading: false });
    dismissModal();
    this.setState({ planInfoSectionIsExpanded: false });
  };

  onConfirm = () => {
    this.setState({ isCTALoading: true });
    const { submitPlanChange, membershipState, immediateUpgradePlanPreview } = this.props;

    const inventoryEligibilityChange = MembershipHelpers.willInventoryEligibilityChange(
      membershipState,
      this.state.upgradeTier
    );

    submitPlanChange({
      tierId: this.state.upgradeTier.id,
      promoCode: immediateUpgradePlanPreview?.promoCode,
      inventoryEligibilityChange,
      onSuccessCallback: this.onDismissModal,
    });
  };

  // renderers

  renderHeading = () => {
    const {
      membershipState: { monthlyShipmentLimit },
    } = this.props;
    const heading = monthlyShipmentLimit > 1 ? "Add a Shipment to Your Plan" : "Upgrade Your Plan";
    const subHeading =
      monthlyShipmentLimit > 1 ? (
        <span>
          You can downgrade anytime from <br></br>your Membership Settings.
        </span>
      ) : (
        "Contact us to downgrade anytime."
      );

    return (
      <div className={`${sectionContainerBaseClass} text-center`}>
        <h3 className="">{heading}</h3>
        <p className="">{subHeading}</p>
      </div>
    );
  };

  renderNewPlan = () => {
    const {
      membershipState: { monthlyShipmentLimit },
    } = this.props;

    const oneSwapUser = monthlyShipmentLimit === 1;

    const shipmentPerMonthText = oneSwapUser
      ? "2 shipments + full closet access"
      : `${monthlyShipmentLimit + 1} shipments per month`;

    const tooltipContent = !oneSwapUser
      ? `Your 1 extra shipment will bring your bill to ${retailPriceFormatted(this.state.sub)}/month.`
      : `Upgrading to 2 or more shipments gets you access to our full inventory, including eventwear & premium styles with a retail value up to $3500. Your new bill will be ${retailPriceFormatted(
          this.state.sub
        )}/month.`;

    return (
      <div className={`${sectionContainerBaseClass} text-left bg-pearl`}>
        <p className={sectionHeaderBaseClass}>New Plan</p>
        <div className="flex flex-col">
          <div className="flex items-start gap-2">
            <span>{shipmentPerMonthText}</span>
            <button
              onClick={this.togglePlanInfoExpandedState}
              aria-label="Click to view more details about your new plan">
              <QuestionMarkIcon />
            </button>
          </div>
          <div
            className={`
              overflow-hidden transition-all duration-300 ease-in-out 
              ${this.state.planInfoSectionIsExpanded ? "mt-2 max-h-10" : "max-h-0"}
            `}>
            {tooltipContent}
          </div>
        </div>
      </div>
    );
  };

  renderBilling = () => {
    const { membershipBag, flagsAndExperiments } = this.props;

    if (membershipBag?.isLoading) {
      return (
        <div className={`${sectionContainerBaseClass} text-left bg-white`}>
          <p className={sectionHeaderBaseClass}>Billing</p>
          <span className="w-full text-center">Just a moment...</span>
        </div>
      );
    }
    const paymentProfiles = membershipBag?.paymentProfiles;
    let billingMethod;

    // check for FE and BE flag, BE responsible for setting and returning the recurring property
    if (flagsAndExperiments?.[flagsAndExperimentNames.COMET_APPLE_PAY_SUBSCRIPTION]) {
      billingMethod = paymentProfiles?.find(profile => profile?.recurring === true);
    } else {
      billingMethod = paymentProfiles?.find(profile => profile?.default === true);
    }

    let ccString = paymentProfileDisplayString(billingMethod);
    let cardSubType = PaymentMethods.subType.CREDIT_CARD;

    let showPaymentUnsupportedMessage = false;
    if (billingMethod?.subType === PaymentMethods.subType.APPLE_PAY) {
      ccString = APPLE_PAY_STRING;
      cardSubType = PaymentMethods.subType.APPLE_PAY;

      /**
       * if the default card is Apple Pay and the browser doesn't support it, we should
       * show the user a prompt to send them to the membership billing page so they can
       * update their card.
       */
      if (!window.ApplePaySession) {
        showPaymentUnsupportedMessage = true;
      }
    }

    const billingInfoCopy = billingMethod ? ccString : "Please Update Your Billing Information";
    // https://renttherunway.jira.com/browse/COMETS-4913 to update AtomCreditCardIcon to handle Apple Pay
    const creditCardIcon = billingMethod ? (
      <AtomCreditCardIcon optionalClassName="m-0" cardType={billingMethod.cardType} cardSubType={cardSubType} />
    ) : null;

    return (
      <div className={`${sectionContainerBaseClass} text-left bg-white`}>
        <p className={sectionHeaderBaseClass}>Billing</p>
        <div className="flex gap-2 items-center">
          {creditCardIcon}
          <span className={`${showPaymentUnsupportedMessage ? "text-mutedRed" : ""}`}>{billingInfoCopy}</span>
          {showPaymentUnsupportedMessage && <ExclamationIcon />}
        </div>

        {showPaymentUnsupportedMessage && (
          <p className="text-sm text-mutedRed mt-2">
            The device or browser you&rsquo;re using does not currently support this payment method. Please change your
            payment method to proceed. <a href={User.Settings}>Manage Membership Billing</a>
          </p>
        )}
      </div>
    );
  };

  renderPromoCodeError() {
    const { immediateUpgradePlanPreview, immediateUpgradePlanPreviewLoading, immediateUpgradePlanPreviewFail } =
      this.props;

    if (immediateUpgradePlanPreview && !immediateUpgradePlanPreviewLoading && immediateUpgradePlanPreviewFail) {
      return (
        <div className={`${sectionContainerBaseClass} text-white text-center bg-mutedRed`}>
          Whoops! Invalid promo code.
        </div>
      );
    }
    return null;
  }

  renderDue = () => {
    const renderPromoCodeInput = () => {
      const { getChangePlanPreview, immediateUpgradePlanPreview, immediateUpgradePlanPreviewLoading } = this.props;
      const appliedPromoCode = immediateUpgradePlanPreview?.promoCode;

      return (
        <ExpressBagPromoCode
          value={appliedPromoCode}
          isLoading={immediateUpgradePlanPreviewLoading}
          onSubmitCode={promoCode => getChangePlanPreview(this.state.upgradeTier.id, promoCode)}
          clearableCode={true}
        />
      );
    };

    const sectionClass = "flex p-5 justify-between text-left border-b-2 border-lighterGray";
    return (
      <div className={`flex flex-col w-full text-left bg-white p-0 gap-0`}>
        <div className={sectionClass}>
          <p className={sectionHeaderBaseClass}>Due Today</p>
          {!this.state.promoFieldOpen && (
            <button onClick={() => this.setState({ promoFieldOpen: true })} className="text-sm underline font-normal">
              Add Promo Code
            </button>
          )}
        </div>
        {this.state.promoFieldOpen && (
          <div className="flex gap-2 px-5 items-center border-b-2 border-lighterGray">
            <span className="text-sm font-bold">Promo Code</span>
            {renderPromoCodeInput()}
          </div>
        )}
        <div className={`${sectionClass} flex-col !text-sm gap-3 font-normal !py-4`}>
          <SummaryItem
            classNameOverrides={{ summaryItem: "!text-black" }}
            amountCharged={formattedPrice(this.state.prePromo ? this.state.prePromo : this.state.preTax)}
            displayText="Extra Shipment"
          />
          {this.state.promo && !isZeroAmount(this.state.promo) && (
            <SummaryItem
              classNameOverrides={{ summaryItem: "!text-mutedRed" }}
              amountCharged={`${formattedPrice(this.state.promo)}${this.state.promoTermCount > 1 ? "/month" : ""}`}
              displayText="Promo Savings"
            />
          )}
          {this.state.promoTermCount > 1 && (
            <div className="sub-line">
              <p>Applied to your next {this.state.promoTermCount} months!</p>
            </div>
          )}
          <SummaryItem
            amountCharged={formattedPrice(this.state.tax)}
            classNameOverrides={{ summaryItem: "!text-black" }}
            displayText="Tax"
          />
        </div>
        <SummaryItem
          additionalClassName="flex p-5 justify-between text-left w-full font-normal text-sm"
          classNameOverrides={{ summaryItem: "!text-black", originalTotal: "hidden" }}
          amountCharged={formattedPrice(this.state.total)}
          displayText="Total"
        />
      </div>
    );
  };

  renderConfirmButton = () => {
    const renderApplePay = () => {
      if (!this.state?.total) return null;

      return (
        <div className="flex flex-col w-full gap-2 items-center">
          <ApplePaySectionMembership
            className="w-full"
            applePayStyle={{
              backgroundColor: "#420022",
              color: "white",
              borderRadius: 8,
            }}
            getLineItems={this.getApplePayLineItems}
            onSuccess={this.onConfirm}
          />
          {this.state.displayCtaInternal && (
            <>
              <div className="or"></div>
              <RTRButton fullWidth={true} disabled={false} onClick={this.onConfirm}>
                {this.state.ctaText}
              </RTRButton>
            </>
          )}
        </div>
      );
    };
    return (
      <div className={`${sectionContainerBaseClass} gap-5 bg-white`}>
        <p className=" text-sm font-normal">
          By clicking below, you authorize RTR to charge the card on file the above monthly rate, or new rate in effect,{" "}
          <b>automatically </b>each month until you cancel. To change or cancel your plan, visit Your Membership page.
        </p>
        {this.state.isApplePayEnabled && renderApplePay()}
        {this.state.isCtaEnabled && (
          <RTRButton
            fullWidth={true}
            loading={this.state.isCTALoading}
            disabled={!this.state.total || this.state.disableCtaBtn}
            onClick={this.onConfirm}>
            {this.state.ctaText}
          </RTRButton>
        )}
      </div>
    );
  };

  render() {
    const {
      membershipState,
      oopsModal,
      immediateUpgradePlanPreview,
      immediateUpgradePlanPreviewLoading,
      immediateUpgradePlanPreviewFail,
      membershipBag,
      flagsAndExperiments,
    } = this.props;

    // Don't render if ineligible
    if (!MembershipHelpers.isEligibleForImmediateSwapUpgrade(membershipState)) {
      return null;
    }

    // If data is unavailable, wait till user tries to open before showing error
    if (
      (!immediateUpgradePlanPreview && !immediateUpgradePlanPreviewLoading && immediateUpgradePlanPreviewFail) ||
      (immediateUpgradePlanPreview && !immediateUpgradePlanPreviewLoading && this.state.invoiceIncludeFailed)
    ) {
      oopsModal();
      return null;
    }

    if (flagsAndExperiments?.[flagsAndExperimentNames.COMET_APPLE_PAY_SUBSCRIPTION]) {
      if (!!membershipBag?.paymentProfiles && !this.state.evaluationComplete) {
        this.evaluatePaymentOptions();
      }
    }

    return (
      <Modal
        data-test-id="add-shipment-modal"
        onClose={this.onDismissModal}
        onClick={this.onConfirm}
        isOpen={true}
        modalContainerClassName="sm:overflow-scroll"
        className="bg-offWhite">
        <div className="flex flex-col justify-center h-full gap-2">
          {this.renderHeading()}
          {this.renderPromoCodeError()}
          {this.renderNewPlan()}
          {this.renderBilling()}
          {this.renderDue()}
          {this.renderConfirmButton()}
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = state => {
  return {
    membershipBag: state.membershipBag,
    paymentMethods: state.paymentProfiles,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    fetchMembershipBag: () => dispatch(fetchBag()),
    fetchFlags: () => {
      dispatch(FlagsAndExperimentsActions.fetchFlagOrExperiment(flagsAndExperimentNames.COMET_APPLE_PAY_SUBSCRIPTION));
    },
  };
};
export default compose(
  withFlagsAndExperiments(flagsAndExperimentNames.COMET_APPLE_PAY_SUBSCRIPTION),
  connect(mapStateToProps, mapDispatchToProps)
)(AddShipmentModal);
